import type { Transaction } from "@rocicorp/zero";
import { userFacingError } from "../../authorization";
import type { Schema } from "../../generatedDrizzleSchema";
import { zql } from "../../generatedDrizzleSchema";

type Tx = Pick<Transaction<Schema>, "run">;

// Each concern is locked under the sync modes where the IdP, not the app, owns
// that data:
//   - userProvisioning: any provisioning mode creates accounts on login or via
//     SCIM, so manual invite/(de)activation is locked under "jit",
//     "jit_users_and_groups", and "scim".
//   - groupMembership: only the group-syncing modes own who belongs to a group —
//     and the group's name, which is the key the IdP claim matches on, so
//     renaming is gated here too — so it's locked under "jit_users_and_groups"
//     and "scim"; plain "jit" provisions users only and leaves group membership
//     (and naming) to the app.
//   - groupLifecycle: creating and deleting groups. Under "jit_users_and_groups"
//     admins still create empty groups for the IdP to populate and can delete
//     them, so this is locked only under "scim", where SCIM owns group existence.
export type IdpOwnedConcern =
  | "userProvisioning"
  | "groupMembership"
  | "groupLifecycle";

const LOCKING_SYNC_MODES: Record<IdpOwnedConcern, ReadonlySet<string>> = {
  userProvisioning: new Set(["jit", "jit_users_and_groups", "scim"]),
  groupMembership: new Set(["jit_users_and_groups", "scim"]),
  groupLifecycle: new Set(["scim"]),
};

export function syncModeLocksConcern(
  syncMode: string | null,
  concern: IdpOwnedConcern
): boolean {
  return syncMode !== null && LOCKING_SYNC_MODES[concern].has(syncMode);
}

// The single predicate both the server guard (assertIdpSyncNotActive) and the UI
// hook (useIdpSyncLock) use to decide a concern is locked: returns the syncMode
// of the first provider that owns it, or null if none do.
export function findLockingSyncMode(
  providers: ReadonlyArray<{ syncMode: string | null }>,
  concern: IdpOwnedConcern
): string | null {
  return (
    providers.find((p) => syncModeLocksConcern(p.syncMode, concern))
      ?.syncMode ?? null
  );
}

export async function assertIdpSyncNotActive(
  tx: Tx,
  tenantId: string,
  concern: IdpOwnedConcern
): Promise<void> {
  const providers = await tx.run(zql.ssoProvider.where("tenantId", tenantId));
  if (findLockingSyncMode(providers, concern) !== null) {
    throw userFacingError(
      "RBAC changes are locked while IdP sync is active. Disable sync to make changes."
    );
  }
}
